import { NAMESPACE } from '@main/shared/utils';
import { ColumnSizingState, OnChangeFn } from '@tanstack/react-table';
import { useEffect, useState } from 'react';

import { useTableSettingsNamespace } from '../../utils';
import {
  emptyLegacyColumnIdMap,
  LegacyColumnIdMap,
  resolveLegacyColumnIdKeys,
} from './legacy-column-ids';

export function useTableColumnSizing(tableName: string, tableId?: string, saveDebounceMs = 16) {
  const settingsNamespace = useTableSettingsNamespace();

  return useTableColumnSizingForNamespace(tableName, settingsNamespace, tableId, saveDebounceMs);
}

export function useTableColumnSizingForNamespace(
  tableName: string,
  settingsNamespace: string | undefined,
  tableId?: string,
  saveDebounceMs = 16,
  legacyColumnIdMap: LegacyColumnIdMap = emptyLegacyColumnIdMap,
) {
  const keyPostfix = settingsNamespace
    ? `:${settingsNamespace}:column-sizing-v2`
    : `:column-sizing`;
  const generalKey = `${NAMESPACE}${tableName}${keyPostfix}`;
  const columnSizingKey = tableId ? `${NAMESPACE}${tableName}:${tableId}${keyPostfix}` : generalKey;

  const [columnSizing, setColumnSizing] = useState(
    JSON.parse(
      localStorage.getItem(columnSizingKey) || localStorage.getItem(generalKey) || '{}',
    ) as ColumnSizingState,
  );

  useEffect(() => {
    // The resolver returns the same object when nothing changes, so this settles after one pass.
    setColumnSizing((sizing) => resolveLegacyColumnIdKeys(sizing, legacyColumnIdMap));
  }, [legacyColumnIdMap]);

  const hasColumnSizingChanged = Object.keys(columnSizing).length > 0;

  const onColumnSizingChange: OnChangeFn<ColumnSizingState> = setColumnSizing;

  useEffect(() => {
    const timeout = setTimeout(
      () => localStorage.setItem(columnSizingKey, JSON.stringify(columnSizing)),
      saveDebounceMs,
    );
    return () => clearTimeout(timeout);
  }, [columnSizing, columnSizingKey, saveDebounceMs]);

  return { columnSizing, hasColumnSizingChanged, onColumnSizingChange };
}
